import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import LoadingComponent from '../shared/components/LoadingComponent';
import FooterNavigation from '../shared/components/Navigation';
import Pagination from '../shared/components/Pagination';
import { useLocationSelection } from '../shared/hooks/useLocationSelection';
import { useLayoutContext } from '../shared/hooks/useLayoutContext';
import ImmersiveLocationDisplay from '../layouts/immersive/components/ImmersiveLocationDisplay';
import LocationDisplay from '../layouts/centered/components/LocationDisplay';
import SplitLocationDisplay from '../layouts/split-screen/components/SplitLocationDisplay';
import SplitNavigation from '../layouts/split-screen/components/SplitNavigation';

export interface Location {
  id: string;
  city: string;
  address: string;
  phone: string;
  country: string;
  zip: string;
  timeZone: string;
  lat: string;
  long: string;
}

interface LocationStepProps {
  selectedLocation: Location | null;
  selectedLocations?: Location[];
  setSelectedLocation: (location: Location | Location[] | null) => void;
  onNext: () => void;
  onPrev: () => void;
  onSelectAll?: (locations: Location[]) => void;
  showLocationsSummary?: boolean;
  nodeData?: any;
}

const LocationStep: React.FC<LocationStepProps> = ({
  selectedLocation: externalSelectedLocation,
  setSelectedLocation: externalSetSelectedLocation,
  onNext,
  onPrev,
  selectedLocations: externalSelectedLocations = [],
  nodeData = null,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE
  const { layoutType } = useLayoutContext();
  const DisplayComponent = layoutType === 'split-screen' ? SplitLocationDisplay : LocationDisplay;

  // Set default nodeData if none is provided
  const defaultNodeData = {
    label: t('booking.steps.location'),
    settings: {
      showMap: true,
      displayStyle: "cards",
      itemsPerRow: 3,
      numberRows: 2,
      colors: {
        cardBackground: "#000000F2",
        cardBorder: "#DDB88919",
        cardBorderSelected: "#22c55e49",
        cardShadow: "rgba(221,184,137,0.07)",
        cardShadowSelected: "rgba(34,197,94,0.25)",
        titleText: "#DDB889",
        subtitleText: "#9CA3AF",
        infoText: "#D1D5DB",
        iconColor: "#DDB889B2",
        selectionBackground: "#22c55e",
        selectionIcon: "#FFFFFF",
        mapBackground: "#00000066",
        mapPinColor: "#DDB88966"
      }
    }
  };

  const effectiveNodeData = nodeData || defaultNodeData;
  // Use our custom hook for location selection logic
  const {
    locations,
    isLoading,
    pageInfo,
    handlePageChange,
    handleLocationSelect
  } = useLocationSelection({
    // Calculate items per page as itemsPerRow × numberRows
    // This ensures we fetch exactly the right number of items to fill the configured grid
    itemsPerPage: layoutType === 'immersive'
      ? 4
      : (effectiveNodeData?.settings?.itemsPerRow || 2) * (effectiveNodeData?.settings?.numberRows || 1)
  });

  // Handle selection (using the prop callback)
  const handleSelect = (location: Location) => {
    handleLocationSelect(location);
    externalSetSelectedLocation(location);
  };

  if (layoutType === 'immersive') {
    return (
      <ImmersiveLocationDisplay
        locations={locations}
        selectedLocations={externalSelectedLocation}
        onLocationClick={(location) => {
          handleSelect(location);
          setTimeout(() => onNext(), 300);
        }}
        nodeData={effectiveNodeData}
        hasNextPage={pageInfo?.hasNextPage ?? false}
        hasPrevPage={pageInfo?.hasPreviousPage ?? false}
        onNextPage={() => handlePageChange('next')}
        onPrevPage={() => handlePageChange('previous')}
      />
    );
  }

  return (
    <motion.div
      className="lp-mx-auto lp-space-y-6 lp-relative lp-overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {isLoading ? (
        <div className="lp-flex lp-items-center lp-justify-center lp-h-48">
          <LoadingComponent />
        </div>
      ) : (
        <motion.div
          className="lp-space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {layoutType === 'split-screen' ? (
            <motion.div
              className="lp-mb-10"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p
                className="lp-uppercase lp-tracking-widest lp-text-xs lp-mb-3"
                style={{ color: 'rgba(221,184,137,0.4)' }}
              >
                {t('booking.steps.location')}
              </p>
              <h1
                className="lp-font-boska"
                style={{ color: '#DDB889', fontSize: 52, lineHeight: 1.05 }}
              >
                {t('booking.selectLocation')}
              </h1>
            </motion.div>
          ) : (
            <Pagination
              pageInfo={pageInfo}
              handlePageChange={handlePageChange}
              title={t('booking.selectLocation')}
              Icon={MapPin}
              showNavigation={true}
            />
          )}

          <DisplayComponent
            locations={locations}
            selectedLocations={externalSelectedLocation === null ? externalSelectedLocations : externalSelectedLocation}
            onLocationClick={handleSelect}
            isClickable={true}
            nodeData={effectiveNodeData}
          />

          {layoutType === 'split-screen' ? (
            <SplitNavigation
              onNext={onNext}
              onPrev={onPrev}
              disabled={!externalSelectedLocation && externalSelectedLocations.length === 0}
              colors={{ accent: effectiveNodeData?.settings?.colors?.selectionBackground }}
            />
          ) : (
            <FooterNavigation
              onNext={onNext}
              onPrev={onPrev}
              disabled={!externalSelectedLocation && externalSelectedLocations.length === 0}
            />
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default LocationStep;
